// iter-121.118 — EA "For Your Information" tabs
//
// 3 tabs (Engagement / AI & Queries / Data & Reliability), each with
// 4 KPI cards + a tab-specific Top-10 ranked table.
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useEaScope } from "@/lib/ea-scope";
import KpiCard from "./KpiCard";
import WhatsNewWidget from "../WhatsNewWidget";

const TABS = [
    { value: "engagement",       label: "Engagement",       metric: "dashboards_by_views" },
    { value: "ai_queries",       label: "AI & Queries",     metric: "query_topics" },
    { value: "data_reliability", label: "Data & Reliability", metric: "datasets_by_rows" },
    { value: "whats_new",        label: "What's new" },
];

function MetricLabel({ metric }) {
    const map = {
        dashboards_by_views: "Top 10 Dashboards by Views",
        query_topics:        "Top 10 Query Topics",
        datasets_by_rows:    "Top 10 Datasets by Rows Processed",
    };
    return <span>{map[metric] || metric}</span>;
}

function TopListTable({ metric, queryString }) {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        setRows(null);
        api.get(`/home/ea/top-list?metric=${metric}&limit=10&${queryString}`)
            .then((r) => setRows(r.data?.rows || []))
            .catch(() => setRows([]));
    }, [metric, queryString]);

    const valueFormat = useMemo(() => {
        // Datasets-by-rows + query-topics + dashboards-by-views are all integers
        return (n) => Number(n).toLocaleString();
    }, []);

    if (rows === null) {
        return <div className="text-xs text-muted-foreground py-6 text-center">Loading…</div>;
    }
    if (rows.length === 0) {
        return <div className="text-xs text-muted-foreground py-6 text-center"
                    data-testid={`ea-top-empty-${metric}`}>No activity in the selected scope.</div>;
    }
    return (
        <div className="border border-border rounded-sm overflow-hidden"
             data-testid={`ea-top-table-${metric}`}>
            <div className="px-3 py-2 bg-secondary/30 flex items-center justify-between border-b border-border">
                <div className="eyebrow text-[10px]"><MetricLabel metric={metric} /></div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {rows.length} {rows.length === 1 ? "item" : "items"}
                </div>
            </div>
            <table className="w-full text-[12px]">
                <thead className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="text-left px-3 py-1.5 w-8">#</th>
                        <th className="text-left px-3 py-1.5">Name</th>
                        <th className="text-right px-3 py-1.5">Value</th>
                        <th className="text-right px-3 py-1.5 hidden sm:table-cell">Secondary</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r.id || i} className="border-t border-border"
                            data-testid={`ea-top-row-${metric}-${i}`}>
                            <td className="px-3 py-1.5 text-muted-foreground">{i + 1}</td>
                            <td className="px-3 py-1.5 font-medium truncate max-w-[280px]" title={r.name}>{r.name}</td>
                            <td className="px-3 py-1.5 text-right font-mono tabular-nums">
                                {valueFormat(r.value)}
                            </td>
                            <td className="px-3 py-1.5 text-right text-muted-foreground hidden sm:table-cell">
                                {r.secondary
                                    ? `${r.secondary.value} ${r.secondary.label}`
                                    : "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function TabBody({ tab, metric }) {
    const { queryString } = useEaScope();
    const [cells, setCells] = useState(null);

    useEffect(() => {
        setCells(null);
        api.get(`/home/ea/kpi-hero?tab=${tab}&${queryString}`)
            .then((r) => setCells(r.data?.cells || []))
            .catch(() => setCells([]));
    }, [tab, queryString]);

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                 data-testid={`ea-info-kpi-grid-${tab}`}>
                {cells === null
                    ? [1, 2, 3, 4].map((i) => <div key={i} className="h-32 shimmer rounded-sm" />)
                    : cells.map((c) => <KpiCard key={c.key} cell={c} />)}
            </div>
            <TopListTable metric={metric} queryString={queryString} />
        </div>
    );
}

export default function EaInfoTabs({ onEmpty }) {
    const [tab, setTab] = useState("engagement");
    useEffect(() => { onEmpty && onEmpty(false); }, [onEmpty]);
    return (
        <div data-testid="ea-info-tabs">
            <div className="eyebrow text-[10px] mb-2">For Your Information</div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList data-testid="ea-info-tabs-list">
                    {TABS.map((t) => (
                        <TabsTrigger key={t.value} value={t.value}
                                     data-testid={`ea-info-tab-${t.value}`}>
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {TABS.map((t) => (
                    <TabsContent key={t.value} value={t.value} className="mt-3"
                                 data-testid={`ea-info-tab-content-${t.value}`}>
                        {tab === t.value && (t.value === "whats_new"
                            ? <WhatsNewWidget />
                            : <TabBody tab={t.value} metric={t.metric} />)}
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}
